import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import EducationModal from '../components/EducationModal';
import Accordion from '../components/Accordion';
import LearningDashboard from '../components/LearningDashboard';
import { MyFavoritesList, MyWishlistList } from '../system-modules/favorites/components';
import BuyerInbox from '../system-modules/favorites/components/BuyerInbox';

export default function BuyerDashboard() {
  const { user } = useAuth();
  const [creditBalances, setCreditBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('credits');
  const [showEducation, setShowEducation] = useState(false);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const accordionRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [accordionKey, setAccordionKey] = useState(0);

  // Fetch unread count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch('/api/favorites/communication/unread/count', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.count || 0);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };
    fetchUnreadCount();
  }, []);

  // Set the active tab based on the query parameter OR unread messages
  useEffect(() => {
    if (initialTab === 'learning') {
      setActiveTab('learning');
    } else if (initialTab === 'inbox') {
      setActiveTab('inbox');
    } else if (initialTab === 'favorites') {
      setActiveTab('favorites');
    } else if (initialTab === 'wishlist') {
      setActiveTab('wishlist');
    } else if (unreadCount > 0) {
      // Auto-open inbox if there are unread messages
      setActiveTab('inbox');
    } else {
      // Default to credits if no tab specified and no unread messages
      setActiveTab('credits');
    }
    // Increment key to force accordion re-render
    setAccordionKey(prev => prev + 1);
  }, [initialTab, unreadCount]);

  // Scroll to inbox tab when it's opened
  useEffect(() => {
    if (activeTab === 'inbox' && accordionRef.current) {
      setTimeout(() => {
        const inboxElement = document.getElementById('accordion-inbox');
        if (inboxElement) {
          inboxElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchCreditData();
  }, []);

  const fetchCreditData = async () => {
    setLoading(true);
    try {
      const [balancesRes, txRes] = await Promise.all([
        fetch('/api/credits/balances', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/credits/transactions', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      const balances = await balancesRes.json();
      const txData = await txRes.json();
      setCreditBalances(balances);
      setTransactions(txData.transactions || []);
    } catch (error) {
      console.error('Failed to fetch credit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalCredits = creditBalances.reduce((sum, b) => sum + b.balance, 0);
  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  // Map tab to accordion index
  const getDefaultOpenIndex = () => {
    const tabMap = {
      'learning': 0,
      'credits': 1,
      'transactions': 2,
      'favorites': 3,
      'wishlist': 4,
      'inbox': 5
    };
    return tabMap[activeTab] !== undefined ? tabMap[activeTab] : null;
  };

  const accordionItems = [
    {
      id: 'learning',
      icon: '📚',
      title: 'Your Learning',
      content: <LearningDashboard userId={user?._id} role="buyer" />
    },
    {
      id: 'credits',
      icon: '💰',
      title: 'Credit Balances',
      badge: `$${totalCredits.toFixed(2)}`,
      content: (
        <div>
          {creditBalances.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No credits available.
              <p className="text-sm mt-1">Complete educational modules from producers, hubs, and educators to earn credits!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {creditBalances.map((balance, idx) => (
                <div key={idx} className="border rounded-lg p-3 flex justify-between items-center">
                  <div>
                    <div className="font-medium">{balance.issuerName}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        balance.issuerType === 'educator' ? 'bg-purple-100 text-purple-800' : 
                        balance.issuerType === 'producer' ? 'bg-green-100 text-green-800' :
                        balance.issuerType === 'hub' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {balance.issuerType === 'educator' ? '📚 Content' : 
                         balance.issuerType === 'producer' ? '🌱 Produce' : 
                         balance.issuerType === 'hub' ? '🏪 Hub' : 'Platform'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {balance.issuerType === 'educator' ? `Redeemable for educational content from ${balance.issuerName}` :
                       balance.issuerType === 'producer' ? `Redeemable for fresh produce from ${balance.issuerName}` :
                       balance.issuerType === 'hub' ? `Redeemable for products from ${balance.issuerName}` :
                       `Redeemable at ${balance.issuerName}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-green-600">${balance.balance.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      id: 'transactions',
      icon: '📋',
      title: 'Transaction History',
      badge: transactions.length,
      content: (
        <div>
          {transactions.length === 0 ? (
            <div className="text-center py-4 text-gray-500">No transactions yet.</div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div key={tx._id} className="border rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{tx.description}</div>
                      <div className="text-sm text-gray-500">{formatDate(tx.createdAt)}</div>
                      {tx.sourceType === 'purchase' && tx.sourceId && (
                        <Link to={`/order-confirmation/${tx.sourceId}`} className="text-blue-600 hover:underline text-xs">
                          View Order
                        </Link>
                      )}
                    </div>
                    <div className={`font-semibold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      id: 'favorites',
      icon: '❤️',
      title: 'My Favorites',
      badge: 'Vendors',
      content: <MyFavoritesList />
    },
    {
      id: 'wishlist',
      icon: '⭐',
      title: 'My Wishlist',
      badge: 'Products',
      content: <MyWishlistList />
    },
    {
      id: 'inbox',
      icon: '📬',
      title: 'Inbox',
      badge: unreadCount > 0 ? `${unreadCount} new` : 'Messages',
      content: <BuyerInbox />
    }
  ];

  // Add IDs to accordion items for scrolling
  const accordionItemsWithIds = accordionItems.map((item, index) => ({
    ...item,
    content: (
      <div id={`accordion-${item.id}`}>
        {item.content}
      </div>
    )
  }));

  return (
    <div className="max-w-4xl mx-auto px-4 py-4" ref={accordionRef}>
      <h1 className="text-2xl font-bold mb-2">Buyer Dashboard</h1>
      <p className="text-gray-600 mb-4">Welcome back, {user?.name}!</p>

      {/* Credit Summary Card */}
      <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg shadow p-6 text-center mb-6">
        <div className="text-sm text-gray-600 mb-1">Your Total Credits</div>
        <div className="text-4xl font-bold text-green-600">{totalCredits}</div>
        <div className="text-xs text-gray-500 mt-1">1 credit = $1.00</div>
      </div>

      <Accordion 
        key={accordionKey}
        items={accordionItemsWithIds} 
        defaultOpen={getDefaultOpenIndex()}
        className="mt-4"
        headerClassName="text-base font-medium py-4"
        iconOpen="▼"
        iconClosed="▶"
      />
    </div>
  );
}
